import { forwardRef, memo, type HTMLAttributes } from 'react'
import { AlertTriangle, RotateCcw, type LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from './button'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

interface ErrorStateAction {
  label: string
  onClick: () => void
  icon?: LucideIcon
}

interface ErrorStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Inside a dialog or a small card: half the vertical room (30.10.2026, was page classes). */
  compact?: boolean
  /** Icon in the error medallion. Defaults to AlertTriangle. */
  icon?: LucideIcon
  /** Heading. Defaults to t('common.errorTitle') — "Noe gikk galt". */
  title?: string
  /** Body copy. Defaults to t('common.errorDescription'). */
  description?: string
  /** Wires the primary "try again" button. Label defaults to t('common.retry'). */
  onRetry?: () => void
  retryLabel?: string
  /** Optional extra action rendered as a secondary button. */
  secondaryAction?: ErrorStateAction
}

/**
 * ErrorState — the failure counterpart to EmptyState. Render this when a data
 * fetch FAILS, so "load failed" is never indistinguishable from "no data yet"
 * (survey root cause R3). `role="alert"` announces the failure to assistive
 * tech; pass `onRetry` to wire the primary retry action. All copy is i18n by
 * default (nb-primary) and colors are mode-aware DS tokens.
 *
 * Usage:
 *   if (error) return <ErrorState onRetry={refetch} />
 */
const ErrorStateImpl = forwardRef<HTMLDivElement, ErrorStateProps>(
  (
    { icon: Icon = AlertTriangle, title, description, onRetry, retryLabel, secondaryAction, compact = false, className, ...props },
    ref,
  ) => {
    const { t } = useTranslation()
    const SecondaryIcon = secondaryAction?.icon
    return (
      <div
        ref={ref}
        className={cn('flex flex-col items-center text-center px-4', compact ? 'py-6' : 'py-12', placed(className, 'ErrorStateImpl', props))}
        /* 81e · Red is for a dead end. With `onRetry` this is not one — the way
           out is the button below — so it is a warning, and it is announced
           politely rather than interrupting. 15 of the 16 mount points pass
           `onRetry`; the one that does not (`CreateEventPage:356`) has its way
           out in the form underneath, so red currently belongs to none of them.
           The condition stays anyway: it is what makes the rule checkable. */
        role={onRetry ? undefined : 'alert'}
        aria-live={onRetry ? 'polite' : undefined}
        {...props}
      >
        <div
          className={cn(
            'size-16 rounded-full border border-[var(--border-default)] flex items-center justify-center mb-5',
            onRetry ? 'bg-[var(--status-warning-bg)]' : 'bg-[var(--status-error-bg)]',
          )}
          aria-hidden="true"
        >
          <Icon className={cn('size-8', onRetry ? 'text-[var(--icon-warning)]' : 'text-[var(--icon-error)]')} />
        </div>
        <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
          {title ?? t('common.errorTitle')}
        </h3>
        <p className="text-sm text-[var(--text-secondary)] max-w-[300px] mb-6 leading-relaxed">
          {description ?? t('common.errorDescription')}
        </p>
        {(onRetry || secondaryAction) && (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {onRetry && (
              <Button onClick={onRetry}>
                <RotateCcw className="size-4" />
                {retryLabel ?? t('common.retry')}
              </Button>
            )}
            {secondaryAction && (
              <Button variant="secondary" onClick={secondaryAction.onClick}>
                {SecondaryIcon && <SecondaryIcon className="size-4" />}
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </div>
    )
  },
)
ErrorStateImpl.displayName = 'ErrorState'

export const ErrorState = memo(ErrorStateImpl)
